import {
    Box,
    Card,
    CardContent,
    Divider,
    SvgIcon,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import ComputerDesktopIcon from '@heroicons/react/24/solid/ComputerDesktopIcon';
import DeviceTabletIcon from '@heroicons/react/24/solid/DeviceTabletIcon';
import DevicePhoneMobileIcon from '@heroicons/react/24/solid/DevicePhoneMobileIcon';
import { FunctionComponent, useState } from 'react';
import { ResolvedWidget } from '@/pages/widgetEditor/types';
import { PageConfigElement } from '@/pages/widgetEditor/utils';
import { WidgetPreviewFrame } from '@/pages/widgetEditor/components/WidgetPreviewFrame';

type WidgetPreviewCardProps = {
    resolvedWidgets: ResolvedWidget[];
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
    pageConfig?: PageConfigElement;
};

/** Window widths the preview offers, in px. */
const VIEWPORTS = {
    desktop: { width: 1280, label: 'Desktop', icon: ComputerDesktopIcon },
    tablet: { width: 768, label: 'Tablet', icon: DeviceTabletIcon },
    mobile: { width: 375, label: 'Mobil', icon: DevicePhoneMobileIcon },
};
type Viewport = keyof typeof VIEWPORTS;

/** Live preview: the added instances on one page, at a chosen viewport width. */
export const WidgetPreviewCard: FunctionComponent<WidgetPreviewCardProps> = ({
    resolvedWidgets,
    entryUrl,
    pageConfig,
}) => {
    const [viewport, setViewport] = useState<Viewport>('desktop');

    return (
        <Card>
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {/* Grows and wraps its note, so the toggle keeps its place on the right. */}
                <Box sx={{ flex: '1 1 320px' }}>
                    <Typography variant='h6'>Vorschau</Typography>
                    {resolvedWidgets.length > 0 && (
                        <Typography color='text.secondary' variant='body2'>
                            Konto-, Bestell- und Warenkorbdaten in der Vorschau
                            stammen aus einem Testkonto, nicht aus deinem
                            Sortiment.
                        </Typography>
                    )}
                </Box>
                <ToggleButtonGroup
                    size='small'
                    exclusive
                    value={viewport}
                    onChange={(_, value) => value && setViewport(value)}
                >
                    {Object.entries(VIEWPORTS).map(
                        ([value, { label, icon: Icon }]) => (
                            <ToggleButton
                                key={value}
                                value={value}
                                sx={{ gap: 1.5, px: 3 }}
                            >
                                <SvgIcon fontSize='small'>
                                    <Icon />
                                </SvgIcon>
                                {label}
                            </ToggleButton>
                        )
                    )}
                </ToggleButtonGroup>
            </CardContent>
            <Divider sx={{ width: '100%' }} />

            {/* The stage: the previewed page floats on it like a window. */}
            <Box sx={{ p: { xs: 3, sm: 6 }, bgcolor: 'grey.100' }}>
                {resolvedWidgets.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 160,
                        }}
                    >
                        <Typography color='text.secondary' variant='body2'>
                            Füge ein Widget hinzu, um die Vorschau zu sehen.
                        </Typography>
                    </Box>
                ) : (
                    <WidgetPreviewFrame
                        key={entryUrl}
                        resolvedWidgets={resolvedWidgets}
                        entryUrl={entryUrl}
                        pageConfig={pageConfig}
                        width={VIEWPORTS[viewport].width}
                    />
                )}
            </Box>
        </Card>
    );
};
